import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as DocumentIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import CaseFiles from './CaseFiles';
import DocumentManagementModal from './DocumentManagementModal';
import RegenerateConfirmationDialog from './RegenerateConfirmationDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

interface DocumentFile {
  id: string;
  name: string;
  filename?: string;
  size: number;
  size_bytes?: number;
  modified: string;
  uploaded_at?: string;
  url?: string;
  folder?: string;
  storage_key?: string;
  storage_provider?: string;
  type?: 'generated' | 'uploaded' | 'edited';
  status?: 'pending' | 'ready' | 'error';
  is_operative?: boolean;
  category?: {
    id: string;
    name: string;
    color: string;
  } | null;
  custom_date?: string | null;
  date_explanation?: string;
  notes?: string;
}

interface CaseDocumentsProps {
  caseId: string;
  caseData: any;
  onDocumentChange?: () => void;
  highlightDocumentId?: string;
}

export default function CaseDocuments({ caseId, caseData, onDocumentChange, highlightDocumentId }: CaseDocumentsProps) {
  const { access } = useAuth();
  const navigate = useNavigate();
  const headers = useMemo(() => ({ Authorization: `Bearer ${access}` }), [access]);

  const [documents, setDocuments] = useState<DocumentFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [generatingDocs, setGeneratingDocs] = useState(false);
  const [taskProgress, setTaskProgress] = useState<number>(0);
  const [taskStatus, setTaskStatus] = useState<string>('');
  const [scopingData, setScopingData] = useState<any>(null);

  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [documentManagementModal, setDocumentManagementModal] = useState<{
    open: boolean;
    document: DocumentFile | null;
  }>({
    open: false,
    document: null
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    caseName: '',
    existingFilesCount: 0,
    existingRunsCount: 0
  });

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    document: null as DocumentFile | null
  });

  const [syncing, setSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState<string | null>(caseData?.ms_last_synced || null);
  const [searchTerm, setSearchTerm] = useState('');

  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [downloadStatus, setDownloadStatus] = useState<string>('');

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
    documentId?: string;
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const [pdfPreviewDialog, setPdfPreviewDialog] = useState<{
    open: boolean;
    document: DocumentFile | null;
    url: string | null;
  }>({
    open: false,
    document: null,
    url: null
  });

  // Cleanup object URL when PDF preview dialog closes
  useEffect(() => {
    return () => {
      // Clean up object URL if it exists when dialog closes or component unmounts
      if (pdfPreviewDialog.url && pdfPreviewDialog.url.startsWith('blob:')) {
        window.URL.revokeObjectURL(pdfPreviewDialog.url);
      }
    };
  }, [pdfPreviewDialog.url]);

  // Fetch documents and scoping data
  useEffect(() => {
    const fetchData = async () => {
      if (!caseId) return;

      try {
        setLoading(true);
        const [docsRes, scopingRes] = await Promise.all([
          api.get(`/cases/${caseId}/documents/`, { headers }),
          api.get(`/snfs/scoping/?case_id=${caseId}`, { headers })
        ]);

        setDocuments(docsRes.data || []);
        setScopingData(scopingRes.data);
      } catch (err) {
        console.error('Failed to fetch documents:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [caseId, access, headers]);

  // Poll for task status
  const pollTaskStatus = async (taskId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const { data } = await api.get(`/celery-tasks/${taskId}/`, { headers });

        setTaskProgress(data.progress || 0);
        setTaskStatus(data.status_message || 'Processing...');

        if (data.state === 'SUCCESS') {
          clearInterval(pollInterval);
          setGeneratingDocs(false);
          // Refresh documents
          const { data: newDocs } = await api.get(`/cases/${caseId}/documents/`, { headers });
          setDocuments(newDocs || []);
        } else if (data.state === 'FAILURE') {
          clearInterval(pollInterval);
          setGeneratingDocs(false);
          console.error('Document generation failed');
        }
      } catch (err) {
        console.error('Failed to poll task status:', err);
        clearInterval(pollInterval);
        setGeneratingDocs(false);
      }
    }, 2000);
  };

  // Build ownership dictionary from scoping data
  const buildOwnershipDict = (owners: any[]) => {
    const dict: any = {};
    owners?.forEach(owner => {
      if (owner.cms_owner_id) {
        dict[owner.cms_owner_id] = {
          name: owner.name,
          type: owner.owner_type,
          role: owner.role,
          address: {
            line1: owner.address_line_1,
            city: owner.city,
            state: owner.state,
            zip: owner.zip_code
          },
          facilities_count: owner.num_facilities_in_case
        };
      }
    });
    return dict;
  };

  // Check for existing files and show confirmation if needed
  const handleGenerateAllDocumentsClick = async () => {
    if (!caseData || !scopingData) return;

    try {
      // Check if there are existing generation runs
      const { data: status } = await api.get<{
        has_existing_generation: boolean;
        existing_runs_count: number;
        existing_files_count: number;
        case_name: string;
      }>(`/docgen/${caseId}/generation_status/`, { headers });

      if (status.has_existing_generation) {
        setConfirmDialog({
          open: true,
          caseName: status.case_name,
          existingFilesCount: status.existing_files_count,
          existingRunsCount: status.existing_runs_count
        });
      } else {
        await handleGenerateAllDocuments(false);
      }
    } catch (err) {
      console.error('Failed to check generation status', err);
      await handleGenerateAllDocuments(false);
    }
  };

  // Generate all documents using SNF scoping data
  const handleGenerateAllDocuments = async (forceRegenerate: boolean = false) => {
    if (!caseData || !scopingData) return;

    setGeneratingDocs(true);
    try {
      // Build config from scoping data
      const config = {
        // Use CCNs from facilities in scoping data
        scheme_ccns: scopingData.facilities?.map((f: any) => f.ccn) || [],
        all_ccns: scopingData.facilities?.map((f: any) => f.ccn) || [],

        // Use court field instead of state_division
        court: caseData.court,

        // Lead defendant from owners or case
        lead_defendant: scopingData.owners?.find((o: any) => o.role === 'LEAD')?.name
                        || caseData.lead_defendant
                        || caseData.name,

        // Build ownership dictionary from owners
        ownership_dict: buildOwnershipDict(scopingData.owners),

        // Totals from scoping summary
        total_billed: scopingData.summary?.total_billed || 0,
        total_paid: scopingData.summary?.total_paid || 0,

        // Additional config
        override_map: {},
        include_dba: false,
        ...(forceRegenerate && { force_regenerate: true })
      };

      const response = await api.post(
        `/docgen/${caseId}/generate_docs/`,
        config,
        { headers }
      );

      // Start polling for progress
      const taskId = response.data.task_id;
      pollTaskStatus(taskId);
    } catch (err: any) {
      if (err.response?.status === 409) {
        console.error('Generation requires confirmation', err);
      } else {
        console.error('Failed to generate documents:', err);
      }
      setGeneratingDocs(false);
    }
  };

  // Handle confirmation dialog actions
  const handleConfirmRegenerate = async () => {
    setConfirmDialog({ open: false, caseName: '', existingFilesCount: 0, existingRunsCount: 0 });
    await handleGenerateAllDocuments(true);
  };

  const handleCancelRegenerate = () => {
    setConfirmDialog({ open: false, caseName: '', existingFilesCount: 0, existingRunsCount: 0 });
  };

  // Download single document
  const handleDownload = async (doc: DocumentFile) => {
    try {
      // Check if this is a SharePoint file
      if (doc.storage_provider === 'spo') {
        // Parse storage_key format: "sharepoint://{drive_id}/{item_id}"
        let item_id = doc.storage_key || '';
        if (item_id.startsWith('sharepoint://')) {
          const pathParts = item_id.replace('sharepoint://', '').split('/');
          item_id = pathParts.length >= 2 ? pathParts[pathParts.length - 1] : pathParts[0];
        }

        // For SharePoint files, fetch as blob with auth headers
        const response = await api.get(
          `/cases/${caseId}/documents/sharepoint_preview/?item_id=${item_id}`,
          {
            headers,
            responseType: 'blob'
          }
        );

        // Create blob and trigger download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        // S3 files - get presigned URL for download
        const response = await api.get(
          `/cases/${caseId}/documents/presign/?filename=${encodeURIComponent(doc.storage_key || doc.name)}`,
          { headers }
        );

        const downloadUrl = response.data.url;

        // For local development URLs, fetch as blob to ensure proper download
        const isLocalUrl = downloadUrl.includes('localhost') || downloadUrl.includes('127.0.0.1');

        if (isLocalUrl) {
          // Local development - fetch as blob with auth headers
          // Extract the path from the full URL to use with axios
          const urlObj = new URL(downloadUrl);
          let relativePath = urlObj.pathname + urlObj.search;

          // Remove /api/ prefix if present since axios api instance already includes it
          if (relativePath.startsWith('/api/')) {
            relativePath = relativePath.substring(5); // Remove '/api/'
          }

          const fileResponse = await api.get(relativePath, {
            headers,
            responseType: 'blob'
          });

          const blob = new Blob([fileResponse.data]);
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', doc.name);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } else {
          // Production S3 presigned URL - use directly
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.setAttribute('download', doc.name);
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      }
    } catch (err) {
      console.error('Failed to download document:', err);
    }
  };

  // Download all documents as zip (async task with progress)
  const handleDownloadAll = async () => {
    try {
      setDownloadingAll(true);
      setDownloadProgress(5);
      setDownloadStatus('Initializing download...');

      // Start the async download task
      const startResponse = await api.post(
        `/cases/${caseId}/documents/start_download_all/`,
        {},
        { headers }
      );

      const taskId = startResponse.data.task_id;

      // Poll for task status
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await api.get(
            `/cases/${caseId}/documents/download_all_status/?task_id=${taskId}`,
            { headers }
          );

          const { state, pct, msg, result, download_url } = statusResponse.data;

          // Update progress
          if (pct) setDownloadProgress(pct);
          if (msg) setDownloadStatus(msg);

          if (state === 'SUCCESS') {
            clearInterval(pollInterval);
            setDownloadStatus('Download complete! Starting file download...');

            // Trigger file download
            if (download_url) {
              // Fetch the Django endpoint with auth to get the presigned S3 URL as JSON
              try {
                const response = await api.get(download_url, { headers });

                if (response.data && response.data.url) {
                  // Navigate to the presigned S3 URL (no auth needed, already signed)
                  window.location.href = response.data.url;
                } else {
                  throw new Error('No presigned URL in response');
                }
              } catch (fetchErr) {
                console.error('Failed to get download URL:', fetchErr);
                setSnackbar({
                  open: true,
                  message: 'Failed to start download',
                  severity: 'error'
                });
              }
            }

            setSnackbar({
              open: true,
              message: `Downloaded ${result?.total_documents || 0} documents successfully`,
              severity: 'success'
            });

            setDownloadingAll(false);
          } else if (state === 'FAILURE') {
            clearInterval(pollInterval);
            setDownloadingAll(false);
            setSnackbar({
              open: true,
              message: 'Failed to download documents',
              severity: 'error'
            });
          }
        } catch (pollErr) {
          console.error('Failed to poll download status:', pollErr);
          clearInterval(pollInterval);
          setDownloadingAll(false);
          setSnackbar({
            open: true,
            message: 'Failed to check download status',
            severity: 'error'
          });
        }
      }, 1000); // Poll every second for responsive progress updates
    } catch (err) {
      console.error('Failed to start download:', err);
      setDownloadingAll(false);
      setSnackbar({
        open: true,
        message: 'Failed to start download',
        severity: 'error'
      });
    }
  };

  // Upload document
  const handleUpload = () => {
    setUploadError(null);
    setUploadFiles([]);
    setUploadDialog(true);
  };

  const handleUploadSubmit = async () => {
    if (uploadFiles.length === 0) return;

    setUploading(true);
    setUploadError(null);

    try {
      // Upload files one by one (could be parallelized if needed)
      for (const file of uploadFiles) {
        const formData = new FormData();
        formData.append('file', file);

        await api.post(
          `/cases/${caseId}/documents/upload/`,
          formData,
          {
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }

      // Refresh documents
      const { data: newDocs } = await api.get(`/cases/${caseId}/documents/`, { headers });
      setDocuments(newDocs || []);

      setUploadDialog(false);
      setUploadFiles([]);

      // Notify parent to refresh timeline
      if (onDocumentChange) {
        onDocumentChange();
      }
    } catch (err: any) {
      console.error('Failed to upload document:', err);
      // Display error message from backend
      const errorMessage = err.response?.data?.detail || 'Failed to upload document. Please try again.';
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    const newFiles = Array.from(files);
    setUploadFiles(prev => [...prev, ...newFiles]);
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // Remove a file from upload list
  const handleRemoveFile = (index: number) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Sync documents from SharePoint
  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await api.post(
        `/cases/${caseId}/documents/sync/`,
        {},
        { headers }
      );

      // Update last synced timestamp
      setLastSynced(response.data.synced_at || new Date().toISOString());

      // Refresh documents
      const { data: newDocs } = await api.get(`/cases/${caseId}/documents/`, { headers });
      setDocuments(newDocs || []);

      setSnackbar({
        open: true,
        message: `Synced ${response.data.updated || 0} document(s) from SharePoint`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Failed to sync documents:', err);
      setSnackbar({
        open: true,
        message: 'Failed to sync documents from SharePoint',
        severity: 'error'
      });
    } finally {
      setSyncing(false);
    }
  };

  // Set document as operative
  const handleApprove = async (doc: DocumentFile) => {
    try {
      await api.post(
        `/cases/${caseId}/documents/set_operative/`,
        { file_id: doc.id },
        { headers }
      );

      // Refresh documents to show updated operative status
      const { data: newDocs } = await api.get(`/cases/${caseId}/documents/`, { headers });
      setDocuments(newDocs || []);

      // Notify parent to refresh timeline
      if (onDocumentChange) {
        onDocumentChange();
      }
    } catch (err) {
      console.error('Failed to set document as operative:', err);
      throw err; // Let CaseFiles handle the error display
    }
  };

  // Preview PDF in browser
  const handlePreviewPdf = async (doc: DocumentFile) => {
    console.log('handlePreviewPdf called with:', { storage_provider: doc.storage_provider, storage_key: doc.storage_key });

    // Open modal immediately with loading state
    setPdfPreviewDialog({
      open: true,
      document: doc,
      url: null // null indicates loading state
    });

    try {
      // Check if this is a SharePoint file
      if (doc.storage_provider === 'spo') {
        console.log('This is a SharePoint file');

        // Parse storage_key format: "sharepoint://{drive_id}/{item_id}"
        // Or fall back to using storage_key as item_id directly if format doesn't match
        let item_id = doc.storage_key || '';
        if (item_id.startsWith('sharepoint://')) {
          const pathParts = item_id.replace('sharepoint://', '').split('/');
          item_id = pathParts.length >= 2 ? pathParts[pathParts.length - 1] : pathParts[0];
        }

        console.log('Fetching SharePoint PDF preview for item_id:', item_id);

        // For SharePoint files, we need to fetch as blob with auth headers
        // then create an object URL that the iframe can load
        const response = await api.get(
          `/cases/${caseId}/documents/sharepoint_preview/?item_id=${item_id}`,
          {
            headers,
            responseType: 'blob'
          }
        );

        console.log('Received blob response:', {
          size: response.data.size,
          type: response.data.type,
          headers: response.headers
        });

        // Check if we actually got a blob with content
        if (!response.data || response.data.size === 0) {
          throw new Error('Received empty response from server');
        }

        // Create object URL from blob
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const objectUrl = window.URL.createObjectURL(blob);

        console.log('Created object URL:', objectUrl);

        // Update dialog with the loaded URL
        setPdfPreviewDialog({
          open: true,
          document: doc,
          url: objectUrl
        });
      } else {
        console.log('This is NOT a SharePoint file, using S3 presigned URL');
        // For S3 files, get presigned URL
        const response = await api.get(
          `/cases/${caseId}/documents/presign/?filename=${encodeURIComponent(doc.storage_key || doc.name)}`,
          { headers }
        );

        console.log('Got presigned URL response:', response.data);

        const pdfUrl = response.data.url;

        // Update dialog with the presigned URL
        setPdfPreviewDialog({
          open: true,
          document: doc,
          url: pdfUrl
        });
      }
    } catch (err) {
      console.error('Failed to load PDF preview:', err);
      // Close the dialog on error
      setPdfPreviewDialog({
        open: false,
        document: null,
        url: null
      });
      setSnackbar({
        open: true,
        message: 'Failed to load PDF preview',
        severity: 'error'
      });
    }
  };

  // Open document (for clicking on card)
  const handleEdit = (doc: DocumentFile) => {
    console.log('handleEdit called with doc:', doc);
    const filename = (doc.name || doc.filename || '').toLowerCase();
    console.log('filename:', filename, 'ends with .pdf:', filename.endsWith('.pdf'));

    // Check if it's a PDF - preview it instead of downloading
    if (filename.endsWith('.pdf')) {
      console.log('Calling handlePreviewPdf');
      handlePreviewPdf(doc);
      return;
    }

    // Other file types that should just be downloaded
    const downloadOnlyExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.mp4', '.mov', '.avi', '.zip', '.rar'];
    const isDownloadOnly = downloadOnlyExtensions.some(ext => filename.endsWith(ext));

    if (isDownloadOnly) {
      handleDownload(doc);
      return;
    }

    // If document has an Office protocol URL (ms-word:, ms-excel:, ms-powerpoint:)
    // open it with the protocol handler to launch the desktop app
    if (doc.url && (doc.url.startsWith('ms-word:') || doc.url.startsWith('ms-excel:') || doc.url.startsWith('ms-powerpoint:'))) {
      // Open with the protocol handler (backend has already formatted the URL)
      // Note: Browser will show a "cancelled" request in console - this is normal behavior
      // when handing off to a desktop app via protocol handler (ms-excel://, etc.)
      window.location.href = doc.url;
    } else {
      // Otherwise, open the document management modal
      setDocumentManagementModal({
        open: true,
        document: doc
      });
    }
  };

  // Open edit modal with metadata fields (for "Edit" menu action)
  const handleOpenEditModal = (doc: DocumentFile) => {
    setDocumentManagementModal({
      open: true,
      document: doc
    });
  };

  const handleSaveDocumentName = async (
    documentId: string,
    newName: string,
    categoryId: string | null,
    customDate: string | null,
    dateExplanation: string,
    notes: string
  ) => {
    try {
      const formData = new FormData();
      formData.append('document_id', documentId);
      formData.append('new_name', newName);

      // Add metadata fields
      if (categoryId) {
        formData.append('category_id', categoryId);
      }
      if (customDate) {
        formData.append('custom_date', customDate);
      }
      if (dateExplanation) {
        formData.append('date_explanation', dateExplanation);
      }
      if (notes) {
        formData.append('notes', notes);
      }

      await api.patch(
        `/cases/${caseId}/documents/edit/`,
        formData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Refresh documents
      const { data: newDocs } = await api.get(`/cases/${caseId}/documents/`, { headers });
      setDocuments(newDocs || []);

      // Notify parent to refresh timeline
      if (onDocumentChange) {
        onDocumentChange();
      }
    } catch (err) {
      console.error('Failed to save document:', err);
      throw err;
    }
  };

  // Delete document
  const handleDelete = (doc: DocumentFile) => {
    setDeleteDialog({
      open: true,
      document: doc
    });
  };

  const handleConfirmDelete = async () => {
    const doc = deleteDialog.document;
    if (!doc) return;

    if (!doc.id) {
      console.error('Document missing ID field:', doc);
      alert('Error: Document is missing ID. Please refresh the page and try again.');
      return;
    }

    try {
      await api.delete(
        `/cases/${caseId}/documents/delete/`,
        {
          headers,
          data: { document_id: doc.id }
        }
      );

      // Refresh documents
      const { data: newDocs } = await api.get(`/cases/${caseId}/documents/`, { headers });
      setDocuments(newDocs || []);

      setDeleteDialog({ open: false, document: null });

      // Notify parent to refresh timeline
      if (onDocumentChange) {
        onDocumentChange();
      }
    } catch (err) {
      console.error('Failed to delete document:', err);
    }
  };

  // Delete document from modal
  const handleDeleteDocumentFromModal = async (documentId: string) => {
    if (!documentId) {
      console.error('Document missing ID');
      alert('Error: Document is missing ID. Please refresh the page and try again.');
      return;
    }

    try {
      await api.delete(
        `/cases/${caseId}/documents/delete/`,
        {
          headers,
          data: { document_id: documentId }
        }
      );

      // Refresh documents
      const { data: newDocs } = await api.get(`/cases/${caseId}/documents/`, { headers });
      setDocuments(newDocs || []);

      // Close the modal
      setDocumentManagementModal({ open: false, document: null });

      // Notify parent to refresh timeline
      if (onDocumentChange) {
        onDocumentChange();
      }
    } catch (err) {
      console.error('Failed to delete document:', err);
      throw err;
    }
  };

  // Show loading state while fetching scoping data
  if (loading || !scopingData) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error if no facilities found
  // Handle document reordering
  const handleDocumentsReordered = async (reorderedDocs: DocumentFile[]) => {
    // Update the display_order field on each document object
    const docsWithNewOrder = reorderedDocs.map((doc, index) => ({
      ...doc,
      display_order: index
    }));

    const orders = docsWithNewOrder.map((doc, index) => ({
      document_id: doc.id,
      order: index
    }));

    // Optimistically update local state immediately
    setDocuments(docsWithNewOrder);

    try {
      await api.patch(
        `/cases/${caseId}/documents/update_order/`,
        { orders },
        { headers }
      );

      // Show success message
      setSnackbar({
        open: true,
        message: 'Document order saved successfully',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Failed to update document order:', error);

      // Revert on error - refetch documents
      const docsRes = await api.get(`/cases/${caseId}/documents/`, { headers });
      setDocuments(docsRes.data || []);

      setSnackbar({
        open: true,
        message: `Failed to save order: ${error.response?.data?.detail || error.message}`,
        severity: 'error'
      });
    }
  };

  return (
    <>
      <CaseFiles
        documents={documents}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        loading={loading}
        caseId={caseId}
        showGenerate={false}
        showDownloadAll={true}
        showUpload={true}
        showEdit={true}
        showDelete={true}
        showSync={true}
        enableReordering={true}
        generating={generatingDocs}
        downloadingAll={downloadingAll}
        syncing={syncing}
        lastSynced={lastSynced}
        onGenerate={handleGenerateAllDocumentsClick}
        onDownloadAll={handleDownloadAll}
        onUpload={handleUpload}
        onDownload={handleDownload}
        onCardClick={handleEdit}
        onEdit={handleOpenEditModal}
        onDelete={handleDelete}
        onSync={handleSync}
        onDocumentsReordered={handleDocumentsReordered}
        onRename={handleSaveDocumentName}
        onApprove={handleApprove}
        highlightDocumentId={highlightDocumentId}
        title="Case Documents"
        emptyMessage="No documents uploaded yet. Use the Upload button to add documents to this case."
        generatingMessage={taskStatus || "Processing documents..."}
      />

      {/* Progress indicator during generation */}
      {generatingDocs && taskProgress > 0 && (
        <Box sx={{ px: 3, pb: 2 }}>
          <CircularProgress
            variant="determinate"
            value={taskProgress}
            size={24}
            sx={{ mr: 1 }}
          />
          {taskProgress}% - {taskStatus}
        </Box>
      )}

      {/* Download progress dialog */}
      <Dialog
        open={downloadingAll}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Downloading Documents
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ paddingTop: '24px !important', paddingBottom: '24px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress
              variant="determinate"
              value={downloadProgress}
              size={60}
            />
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {downloadProgress}%
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {downloadStatus}
            </Typography>
            <Alert severity="info" sx={{ mt: 2, width: '100%' }}>
              Please don't leave this page while the download is in progress.
            </Alert>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialogs */}
      <RegenerateConfirmationDialog
        open={confirmDialog.open}
        onClose={handleCancelRegenerate}
        onConfirm={handleConfirmRegenerate}
        caseName={confirmDialog.caseName}
        existingFilesCount={confirmDialog.existingFilesCount}
        existingRunsCount={confirmDialog.existingRunsCount}
      />

      {/* Document Management Modal */}
      <DocumentManagementModal
        open={documentManagementModal.open}
        onClose={() => setDocumentManagementModal({ open: false, document: null })}
        document={documentManagementModal.document}
        caseId={caseId}
        headers={headers}
        onSave={handleSaveDocumentName}
        onDeleteDocument={handleDeleteDocumentFromModal}
      />

      <DeleteConfirmationDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, document: null })}
        onConfirm={handleConfirmDelete}
        filename={deleteDialog.document?.name || ''}
        fileSize={deleteDialog.document?.size}
      />

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialog}
        onClose={() => {
          setUploadDialog(false);
          setUploadFiles([]);
          setUploadError(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Upload Documents
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ paddingTop: '24px !important', paddingBottom: '24px' }}>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {uploadError}
            </Alert>
          )}

          {/* Drag and Drop Zone */}
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: '2px dashed',
              borderColor: isDragging ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              backgroundColor: isDragging ? 'primary.50' : 'grey.50',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'primary.50',
              }
            }}
          >
            <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
              Drag and drop files here
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              or
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadIcon />}
            >
              Browse Files
              <input
                type="file"
                multiple
                hidden
                onChange={(e) => handleFileSelect(e.target.files)}
                accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.jpg,.jpeg,.png"
              />
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
              Supported formats: PDF, DOC, DOCX, TXT, XLSX, XLS, CSV, JPG, JPEG, PNG
            </Typography>
          </Box>

          {/* Selected Files List */}
          {uploadFiles.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Selected Files ({uploadFiles.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {uploadFiles.map((file, index) => (
                  <Chip
                    key={index}
                    label={file.name}
                    icon={<DocumentIcon sx={{ fontSize: 16 }} />}
                    onDelete={() => handleRemoveFile(index)}
                    sx={{
                      maxWidth: '100%',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Total size: {(uploadFiles.reduce((acc, f) => acc + f.size, 0) / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => {
              setUploadDialog(false);
              setUploadFiles([]);
              setUploadError(null);
            }}
            size="large"
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadSubmit}
            variant="contained"
            disabled={uploadFiles.length === 0 || uploading}
            size="large"
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : `Upload ${uploadFiles.length} file${uploadFiles.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog
        open={pdfPreviewDialog.open}
        onClose={() => setPdfPreviewDialog({ open: false, document: null, url: null })}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          py: 2,
          px: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            <DocumentIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {pdfPreviewDialog.document?.name || 'PDF Preview'}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setPdfPreviewDialog({ open: false, document: null, url: null })}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: 'calc(90vh - 120px)', overflow: 'hidden' }}>
          {pdfPreviewDialog.url ? (
            <Box
              component="iframe"
              src={pdfPreviewDialog.url}
              sx={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: '#525659'
              }}
              title={pdfPreviewDialog.document?.name || 'PDF Preview'}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5'
              }}
            >
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Loading PDF...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => pdfPreviewDialog.document && handleDownload(pdfPreviewDialog.document)}
            startIcon={<DownloadIcon />}
            variant="contained"
          >
            Download
          </Button>
          <Button
            onClick={() => setPdfPreviewDialog({ open: false, document: null, url: null })}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            cursor: snackbar.documentId ? 'pointer' : 'default',
            '&:hover': snackbar.documentId ? {
              backgroundColor: snackbar.severity === 'success' ? 'success.dark' : 'error.dark'
            } : {}
          }}
          action={
            snackbar.documentId && (
              <IconButton
                size="small"
                aria-label="view document"
                color="inherit"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/cases/${caseId}?tab=documents&highlight=${snackbar.documentId}`);
                  setSnackbar({ ...snackbar, open: false });
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            )
          }
          onClick={(e) => {
            if (snackbar.documentId) {
              e.stopPropagation();
              navigate(`/cases/${caseId}?tab=documents&highlight=${snackbar.documentId}`);
              setSnackbar({ ...snackbar, open: false });
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}